import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import {
  measureAnalysisRegionRect,
  type AnalysisRegionObjectFit,
  type AnalysisRegionRect,
} from '@safetyhub/shared';
import { deriveRegionDisplay, type AdminMlAnalysisRegion } from '../lib/ml-sources';

/**
 * 중점 분석 영역 오버레이 — **이미지 위에 겹치는 빨간 사각형 하나**.
 *
 * ## 기준은 «측정된 이미지 내용 상자»다 (2026-10-07 P4-h 정정)
 *
 * ⚠️ **종전 전제가 틀려서 박스가 엉뚱한 자리를 가리켰다.** 여기 「부모가 `relative`이고 이미지가
 * `object-cover`/`object-contain`으로 그 박스를 채우는 한 위치가 자동으로 맞는다」고 적혀 있었다.
 * 백분율이 맞는 조건은 **「부모 상자와 이미지 내용 상자의 종횡비가 같다」** 하나이고, 그 두
 * `object-fit` 값은 정의상 **둘이 다를 때** 쓰는 것이다 — cover는 잘라내고 contain은 레터박스를
 * 남긴다. 실제로 `MlPhotoThumbnail`(`aspect-square` + `object-cover`)에서 가로 사진의 박스가
 * 가로로 밀리고 폭이 좁아져 있었다.
 *
 * ⇒ 이제 **측정된 이미지 내용 상자를 기준으로 그린다 — 부모 상자와 이미지 비율이 달라도 맞는다.**
 * 수학은 shared `measureAnalysisRegionRect` **한 곳**(순수·값 spec 보유)이고, 여기가 소유하는 것은
 * **측정과 관측**이다.
 *
 * ## 재측정 트리거 — 하나라도 빠지면 「박스가 엉뚱한 자리」로 돌아온다
 *
 * 종전 JSDoc이 측정을 피한 이유가 정확히 이것이었고(「리사이즈·줌·lazy 로드마다 다시 재야 한다」),
 * 그 걱정은 유효하다. 그래서 넷을 **이 컴포넌트가** 소유한다 — 호스트는 ref만 넘긴다:
 *
 * 1. **이미지 `load` 이벤트** — 두 호출부 다 `loading="lazy"`거나 늦게 교체될 수 있어, 첫 렌더
 *    시점엔 `naturalWidth`가 0이다. `src`가 바뀌어도 이 이벤트가 다시 온다.
 * 2. **`img.complete` 즉시 확인** — 캐시된 이미지는 effect가 붙기 **전에** 로드가 끝나 `load`가
 *    영영 오지 않는다(이 한 줄이 없으면 「새로고침하면 박스가 안 보인다」가 된다).
 * 3. **이미지 요소의 `ResizeObserver`** — 반응형 그리드·창 크기·줌.
 * 4. **오버레이 호스트의 `ResizeObserver`** — 부모가 커졌는데 이미지 크기는 그대로인 배치
 *    (`mx-auto` 등)에서 이미지의 `offsetLeft`만 바뀌는 경우.
 *
 * ⚠️ 측정 전(또는 natural 크기가 0인 동안)에는 **사각형을 그리지 않는다** — 0으로 메워 그리면
 * 박스가 좌상단에 붙어 **「엉뚱한 자리가 위험하다」**고 말한다. 조용한 오표시보다 시끄러운
 * 미표시를 고르는 것이 이 파일의 규율이다(아래 「형상이 틀린 값」 절과 같은 이유).
 *
 * ## 두 요소로 갈려 있다 — testid가 둘인 이유
 *
 * - **오버레이 호스트**(`analysis-region-box`): 존재 = 「유효한 영역이 있어 오버레이 층을 깐다」.
 *   `overflow-hidden`을 **여기가 소유한다** — `cover`는 내용 상자가 요소 밖으로 넘치므로
 *   클리핑이 없으면 박스가 옆 타일 위에 그려진다(그 자체로 거짓 위치다).
 * - **측정된 사각형**(`analysis-region-rect`): 존재 = 「실제로 재서 그렸다」.
 *
 * jsdom은 레이아웃을 계산하지 않아 `clientWidth`·`naturalWidth`가 늘 0이다 — 그래서 **사각형은
 * 테스트 환경에서 렌더되지 않는다**. 판정을 호스트에 남긴 것은 그 때문이다: testid를 사각형
 * 하나에 두면 「형상이 틀린 영역은 그리지 않는다」류 **부재 단언이 공허하게 통과**한다(무엇을
 * 넘겨도 사각형이 없으므로). 좌표가 사진 위에 얹히는지는 이 층에서 원리적으로 못 재니, 그 축의
 * 탐지기는 **shared 값 spec + 브라우저 실측**이다.
 *
 * ## 이중으로 그려지지 않는다
 *
 * 서버가 AI에게 보내는 **마커본**은 `…_marked.jpg`라는 **별도 파생 파일**이고 DB는 그것을 가리키지
 * 않는다(`analysis-region-marker.service.ts` `ANALYSIS_MARKER_SUFFIX`). 즉 `photo.url`·
 * `thumbnailUrl`은 **원본**이라 여기서 그리는 박스가 구운 마커와 겹치지 않는다. 만약 나중에 저장
 * 포인터가 마커본으로 바뀌면 이 오버레이는 **지워야 한다**(그때는 박스가 두 개로 보인다).
 *
 * ## 형상이 틀린 값은 그리지 않는다
 *
 * 판정은 `deriveRegionDisplay` → shared `isValidAnalysisRegion` **단일 판정자**에 위임한다.
 * 틀린 좌표로 그린 박스는 운영자에게 **「엉뚱한 자리가 위험하다」**고 말하므로, 조용한 오표시보다
 * 시끄러운 미표시를 고른다(그 「못 그렸다」 문구는 맥락 패널이 `REGION_INVALID_LABEL`로 적는다 —
 * 여기서 `null`을 돌려주는 것만으로는 아무 말도 하지 않는다는 점에 주의).
 *
 * `aria-hidden`인 이유: 좌표 숫자를 읽어 주는 것은 쓸모가 없고, **그 사실의 텍스트 짝**은 패널의
 * `REGION_MARKED_LABEL` 행이 담당한다.
 */
export function AnalysisRegionBox({
  region,
  imageRef,
  objectFit = 'contain',
  /** 테두리 두께 — 썸네일은 얇게, 확대 이미지는 두껍게. */
  weight = 'thin',
}: {
  region: AdminMlAnalysisRegion | null | undefined;
  /**
   * 박스를 얹을 **이미지 요소** — 측정 대상이다.
   *
   * ⚠️ 호스트가 명시로 넘긴다(부모 DOM에서 `img`를 찾아내지 않는다) — 암묵 탐색은 호스트가
   * 이미지를 하나 더 넣는 날 **조용히 다른 이미지를 재게** 된다.
   */
  imageRef: React.RefObject<HTMLImageElement | null>;
  /** 그 이미지의 `object-fit`. 기본 `contain`(요소가 이미지 비율대로 커지는 자리와 같은 값). */
  objectFit?: AnalysisRegionObjectFit;
  weight?: 'thin' | 'thick';
}) {
  const hostRef = useRef<HTMLSpanElement>(null);
  const [rect, setRect] = useState<AnalysisRegionRect | null>(null);
  const isValid = deriveRegionDisplay(region) === 'valid' && !!region;

  const measure = useCallback(() => {
    const image = imageRef.current;
    const host = hostRef.current;
    if (!image || !host || !region || !isValid) {
      setRect(null);
      return;
    }

    /**
     * ⚠️ **`clientWidth`가 아니라 `getBoundingClientRect()`를 쓴다 — 그쪽은 «정수로 반올림»된다.**
     * 맥락 패널처럼 상자 비율 = 이미지 비율인 자리에서도 반올림이 두 배율을 어긋나게 만들어
     * (예 223.86 → 224) 종전 백분율과 **0.1px쯤 달라진다.** 눈에 보이는 차이는 아니지만,
     * 「공용화가 이미 옳은 자리를 바꾸지 않는다」를 **정확히** 참으로 두는 편이 낫다. web
     * `AnalysisRegionEditor`의 같은 계산도 rect를 쓴다.
     */
    const imageRect = image.getBoundingClientRect();
    const next = measureAnalysisRegionRect(region, {
      elementWidth: imageRect.width,
      elementHeight: imageRect.height,
      naturalWidth: image.naturalWidth,
      naturalHeight: image.naturalHeight,
      objectFit,
    });

    /**
     * 이미지가 호스트 안에서 밀려 있을 수 있다 — 그 offset을 더해야 호스트 좌표계가 된다.
     *
     * ⚠️ `offsetLeft`를 쓰지 않는 이유 둘: **정수 반올림**(위와 같은 이유)과 **기준이
     * `offsetParent`**라는 것이다. 후자는 「호스트와 이미지가 같은 positioned 상자 안에
     * 있고 그 상자에 테두리가 없다」를 **조용히 전제**하는데, 두 rect의 차는 그 전제 없이
     * 늘 맞는다. 이 덧셈이 shared 순수 함수 밖에 있는 이유는 그 값이 DOM 배치에서만 나오기
     * 때문이다(그 함수 JSDoc이 경계를 적는다).
     */
    const hostRect = host.getBoundingClientRect();
    setRect(
      next
        ? {
            ...next,
            left: next.left + (imageRect.left - hostRect.left),
            top: next.top + (imageRect.top - hostRect.top),
          }
        : null,
    );
  }, [imageRef, isValid, objectFit, region]);

  /**
   * ⚠️ **`useLayoutEffect`인 것은 「한 프레임 깜빡임」 때문이다.** `useEffect`는 **페인트 뒤**에
   * 돌므로, 이미지가 **이미 캐시에 있어 마운트 시점에 `complete`인** 경우(목록을 스크롤해
   * 되돌아올 때·패널을 다시 열 때) 박스 없는 프레임이 **한 번 보이고** 그 다음 프레임에 나타난다.
   * 레이아웃 effect는 페인트 전에 돌아 그 틈을 없앤다.
   *
   * ⚠️ 아직 로드되지 않은 이미지는 이것으로도 못 당긴다 — 박스는 `load` 때 나타난다. 그것은
   * **의도**다: 백분율 구현은 로드 전에도 박스를 그려 **빈 자리에 빨간 사각형**이 떴고(그 사진의
   * 종횡비를 모르는 동안이라 어차피 틀린 자리였다), 이 파일의 규율은 **「조용한 오표시보다
   * 시끄러운 미표시」**다. 호스트에 배경·테두리를 두지 않은 것도 같은 이유 — 측정 전에
   * **번쩍일 빈 테두리가 없다**(테두리는 사각형 쪽에만 있다).
   */
  useLayoutEffect(() => {
    const image = imageRef.current;
    if (!image) return;

    // 캐시된 이미지는 `load`가 이미 지나갔다 — 그때는 여기서 한 번 재는 것이 유일한 기회다.
    if (image.complete) measure();

    image.addEventListener('load', measure);

    // 크기 관측이 없는 환경(jsdom 기본)에서도 마운트가 죽지 않게 한다 — admin 테스트 setup이
    // 무동작 스텁을 두지만 그 사실에 의존하지 않는다(`EditorPage`의 같은 가드와 동형).
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(image);
    const host = hostRef.current;
    if (host) observer?.observe(host);

    return () => {
      image.removeEventListener('load', measure);
      observer?.disconnect();
    };
  }, [imageRef, measure]);

  if (!isValid) return null;

  return (
    <span
      ref={hostRef}
      aria-hidden="true"
      data-testid="analysis-region-box"
      // `overflow-hidden`은 **빠뜨릴 수 없다** — `cover`에서 내용 상자가 요소 밖으로 넘치므로
      // 클리핑이 없으면 박스가 이웃 타일 위에 그려진다(위 JSDoc 「두 요소로 갈려 있다」).
      className="absolute inset-0 overflow-hidden pointer-events-none"
    >
      {rect && (
        <span
          data-testid="analysis-region-rect"
          className={`absolute border-red-500 ${
            weight === 'thick' ? 'border-[3px]' : 'border-2'
          }`}
          style={{
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height,
            // ⚠️ **작은 박스를 「안 보이니」 최소 크기로 키우지 말 것 — 없는 위험 범위를 만든다.**
            // dev 실측 8건 중 2건이 `0.02×0.02`(면적 **0.04%**)라 점으로 보이는데, 그것을 키우면
            // 운영자가 **엉뚱한 자리를 위험으로 읽는다**(좌표가 거짓이 되는 순간이다). 대신 바깥
            // 흰 그림자로 **눈에 걸리게만** 한다 — 그림자는 박스 «밖»이라 범위를 바꾸지 않는다.
            boxShadow: '0 0 0 1px rgba(255,255,255,0.9)',
          }}
        />
      )}
    </span>
  );
}
